import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Plan } from '@t/productOffering.types';
import type { ParsedAddOn } from '@t/addOn.types';
import { selectParsedAddOns } from '@utils/addOnMapper';

interface UseAddOnsForPlanResult {
  allAddOns: ParsedAddOn[];
  themePackages: ParsedAddOn[];
  individualChannels: ParsedAddOn[];
  serviceAddOns: ParsedAddOn[];
}

/**
 * Reads the already-fetched productOffering response from the React Query cache,
 * extracts add-ons for the given planId, and returns parsed/grouped add-ons.
 * Does NOT make an API call.
 */
export const useAddOnsForPlan = (sessionId: string, homeId: string, planId: string): UseAddOnsForPlanResult => {
  const queryClient = useQueryClient();

  const cachedPlans = queryClient.getQueryData<Plan[]>(['productOfferings', sessionId, homeId]);

  const plan = cachedPlans?.find((p) => p.planId === planId);

  return useMemo(() => {
    const empty: UseAddOnsForPlanResult = {
      allAddOns: [],
      themePackages: [],
      individualChannels: [],
      serviceAddOns: [],
    };

    // For bundle plans, add-ons live on the pre-computed bundleAddOnMemberAddOns field
    // (the Internet member's optional children). The bundle's own addOns array is always empty.
    const addOnsSource = plan?.isBundle && plan.bundleAddOnMemberAddOns?.length
      ? plan.bundleAddOnMemberAddOns
      : plan?.addOns;
    const categoryForParsing = plan?.isBundle && plan.bundleAddOnMemberCategory
      ? plan.bundleAddOnMemberCategory
      : plan?.typeName;

    if (!addOnsSource?.length || !categoryForParsing) return empty;

    const allAddOns = selectParsedAddOns(addOnsSource, categoryForParsing);

    return {
      allAddOns,
      themePackages: allAddOns.filter((a) => a.type === 'themePackage'),
      individualChannels: allAddOns.filter((a) => a.type === 'individualChannel'),
      serviceAddOns: allAddOns.filter((a) => a.type === 'serviceAddOn'),
    };
  }, [plan]);
};
